import type { AssetSheetColumnMapUpdateInput } from '@atlas/models'
import type { AssetSheetColumnMapEntryDTO } from '@atlas/types'
import { AssetSheetColumnMapScope, AxisCode } from '@atlas/types'
import { CardShell, ErrorState, ListSkeleton, SelectField } from '@atlas/ui-components'
import { Badge, Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLastSync } from '@/hooks/useAssetSheet'
import { columnEditErrorOf, useAssetSheetColumnMap, useUpdateAssetSheetColumn } from '@/hooks/useAssetSheetColumnMap'
import { useClassificationBootstrap } from '@/hooks/useClassification'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { AssetSheetParam } from './asset-sheet-view'
import { ColumnEditDialog, type ColumnEditError } from './ColumnEditDialog'
import { ColumnMapTable } from './ColumnMapTable'

/** One tab's mapped columns, each editable, with the reason the last sync skipped the tab when it did. */
export function ColumnMapTab() {
	const [params, setParams] = useSearchParams()
	const columnMap = useAssetSheetColumnMap()
	const fileTypes =
		useClassificationBootstrap().data?.valuesByAxis[AxisCode.FILE_TYPE]?.map((v) => ({
			value: v.code,
			label: v.label,
		})) ?? []
	const skipped = useLastSync()?.tabProblems ?? []
	const update = useUpdateAssetSheetColumn()
	// The column as it was when opened, so the draft survives the optimistic patch and its rollback.
	const [editing, setEditing] = useState<AssetSheetColumnMapEntryDTO | null>(null)
	const [error, setError] = useState<ColumnEditError | null>(null)

	if (columnMap.isPending) return <ListSkeleton label="Loading the column map" />
	if (columnMap.isError)
		return (
			<ErrorState
				title="Could not load the column map"
				description={apiErrorMessage(columnMap.error, 'Try again.')}
				action={
					<Button variant="secondary" size="sm" onPress={() => void columnMap.refetch()}>
						Try again
					</Button>
				}
			/>
		)

	const tabs = columnMap.data
	const asked = params.get(AssetSheetParam.SHEET_TAB)
	const current = tabs.find((t) => t.tab === asked) ?? tabs[0]
	if (!current) return <p className="text-ds-sm text-ds-text-muted">No tab is mapped yet.</p>
	const problem = skipped.find((p) => p.tab === current.tab)

	const selectTab = (tab: string) =>
		setParams((prev) => {
			const next = new URLSearchParams(prev)
			next.set(AssetSheetParam.SHEET_TAB, tab)
			return next
		})

	const close = () => {
		setEditing(null)
		setError(null)
	}

	const save = (column: AssetSheetColumnMapEntryDTO, input: AssetSheetColumnMapUpdateInput) => {
		const valueLabel = fileTypes.find((f) => f.value === input.valueCode)?.label
		const reach =
			input.scope === AssetSheetColumnMapScope.ALL_TABS ? 'on every tab that maps it' : `on tab ${column.tab}`
		setError(null)
		update.mutate(
			{ column, input, valueLabel },
			{
				onSuccess: ({ auditRecorded }) => {
					close()
					const changed = `${input.columnName ?? column.columnName} changed ${reach}. It applies from the next sync`
					if (auditRecorded) notification.success(`${changed}.`)
					else notification.warning(`${changed}, but the change could not be recorded in the activity log.`)
				},
				onError: (e) => {
					const refusal = columnEditErrorOf(e)
					setError(refusal)
					notification.error(`${column.columnName} was not changed: ${refusal.message}`)
				},
			},
		)
	}

	return (
		<div className="flex flex-col gap-ds-4">
			<TabPicker
				tabs={tabs.map((t) => t.tab)}
				current={current.tab}
				columns={current.columns.length}
				onSelect={selectTab}
			/>
			{problem && <SkippedTabNotice tab={current.tab} reason={problem.reason} />}

			<ColumnMapTable columns={current.columns} onEdit={setEditing} />

			<ColumnEditDialog
				column={editing}
				tabColumns={current.columns}
				sameColumnTabs={editing ? sameColumnTabs(tabs, editing) : 1}
				fileTypes={fileTypes}
				saving={update.isPending}
				error={error}
				onSave={(input) => editing && save(editing, input)}
				onCancel={close}
			/>
		</div>
	)
}

/** How many tabs map a column of this name on this axis, its own included: what "Apply to every tab" reaches. */
const sameColumnTabs = (tabs: { columns: AssetSheetColumnMapEntryDTO[] }[], column: AssetSheetColumnMapEntryDTO) =>
	tabs.filter((t) => t.columns.some((c) => c.columnName === column.columnName && c.axis === column.axis)).length

function TabPicker(props: { tabs: string[]; current: string; columns: number; onSelect: (tab: string) => void }) {
	return (
		<div className="flex flex-col gap-ds-3 sm:flex-row sm:items-end sm:justify-between">
			<div className="flex flex-col gap-ds-3 sm:flex-row sm:items-end">
				<div className="w-40">
					<SelectField
						label="Tab"
						value={props.current}
						options={props.tabs.map((t) => ({ value: t, label: t }))}
						isClearable={false}
						onChange={props.onSelect}
					/>
				</div>
				<p className="max-w-xl text-ds-sm text-ds-text-muted">
					{props.columns} mapped columns. Rows are identified by <code>#</code>, and only rows whose Status is{' '}
					<code>ready</code> are read. Columns not listed here are not read.
				</p>
			</div>
			<Badge variant="muted" size="sm">
				Changes apply from the next sync
			</Badge>
		</div>
	)
}

function SkippedTabNotice({ tab, reason }: { tab: string; reason: string }) {
	return (
		<CardShell as="section" aria-label="Last sync skipped this tab" className="flex flex-col gap-ds-2 border-ds-danger">
			<Badge variant="danger" size="sm" className="self-start">
				Last sync skipped this tab
			</Badge>
			<p className="text-ds-sm text-ds-text">
				{reason}. Rename the column in the workbook, or edit the column here and enter the header tab {tab} uses, for
				this tab only.
			</p>
		</CardShell>
	)
}
